import { trigger } from '@angular/animations';
import { DOCUMENT } from '@angular/common';
import { Component, ComponentRef, HostListener, Inject, ViewChild, ViewContainerRef } from '@angular/core';
import { fadeIf } from '../../animations/fade.if.animation';
import { moveLeftIf } from '../../animations/move-left.if.animation';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import { DialogOptions, DialogsComponentOpenData } from '../../interfaces/dialog-data.interface';
import { debounce } from '../../lib/debounce';
import { getScrollbarWidth } from '../../lib/get-scrollbar-width';
import { sleep } from '../../lib/sleep';
import { disableBodyScroll, enableBodyScroll } from '../../lib/toggle-body-scroll';
import { AnalyticsService } from '../../services/analytics.service';
import { DialogService } from '../../services/dialog.service';
import { SvgComponent } from '../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../shared/directives/button.directive';

@Component({
  selector: 'app-mobile-menus',
  templateUrl: './mobile-menus.component.html',
  styleUrls: ['./mobile-menus.component.scss'],
  animations: [trigger('menuBg', fadeIf), trigger('menuContent', moveLeftIf)],
  standalone: true,
  imports: [ButtonDirective, SvgComponent],
})
export class MobileMenusComponent {
  @ViewChild('placeholder', { read: ViewContainerRef, static: false })
  private viewRef: ViewContainerRef;
  componentRef: ComponentRef<{ dialogName: string; onGetData: (data: unknown, close: unknown) => unknown }>;
  menuOpen = false;
  options: DialogOptions = {};
  data = {};
  openDialogResolve: (...args) => void;
  currentSize: string;
  currentSelector: string;
  component: DialogsComponentOpenData['component'];

  @HostListener('window:resize', ['$event']) winResize() {
    if (!this.menuOpen) return;
    const size = ScreenSizes.screenSize();
    if (this.currentSize && size !== this.currentSize) this.setComponentDebounce();
    this.currentSize = size;
  }
  @HostListener('window:keyup', ['$event']) keyEvent(event: KeyboardEvent) {
    if (event.target['form']) return;
    if (event.key === 'Escape') this.close(undefined);
  }

  constructor(
    private dialog: DialogService,
    private analytics: AnalyticsService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.dialog.onDialogOpen.subscribe(async (params: DialogsComponentOpenData) => {
      if (params?.options.layout !== 'side') return;
      if (this.openDialogResolve) await this.close(undefined);
      this.menuOpen = true;
      this.data = params.data;
      this.options = params.options;
      this.component = params.component;
      this.document.body.style.setProperty('--scrollbar-width', getScrollbarWidth() + 'px');
      disableBodyScroll('mobile-menu');
      await sleep(0);
      if (!this.viewRef) await sleep(100);
      if (!this.viewRef) return;
      this.setComponent();
      this.currentSelector = this.componentRef?.instance?.dialogName;
      if (!this.currentSelector) console.warn('Side menu has no name', this.componentRef.componentType.name);
      this.analytics.track('open_side-menu:' + this.currentSelector, false);
      this.openDialogResolve = params.resolve;
    });
    this.dialog.onDialogClose.subscribe(e => {
      if (e) this.close(false);
    });
  }

  private setComponentDebounce = debounce(() => {
    if (!this.menuOpen) return;
    this.setComponent();
  }, 100);

  private setComponent() {
    if (!this.viewRef) return;
    this.viewRef.clear();
    this.componentRef = this.viewRef.createComponent(this.component);
    this.componentRef.instance.onGetData?.(this.data, this.close.bind(this));
  }

  async close(event: unknown, delay = 200) {
    if (!this.menuOpen) return;
    this.analytics.track('close_side-menu:' + this.currentSelector, false);
    this.openDialogResolve?.(event);
    this.openDialogResolve = undefined;
    this.menuOpen = false;
    const ref = this.componentRef;
    // On mobile we need to wait for the animation to finish before re-enabling the body scroll
    if (ScreenSizes.screenSize() !== 'mobile') await sleep(delay);
    enableBodyScroll('mobile-menu');
    this.document.body.style.setProperty('--scrollbar-width', '0px');
    if (ScreenSizes.screenSize() === 'mobile') await sleep(delay);
    if (ref !== this.componentRef) return;
    this.componentRef?.destroy();
    this.componentRef = undefined;
  }
}
